import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, Select, Button, Alert, Tag, Typography, Space, Spin, Collapse, Table, List } from 'antd';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled,
  InfoCircleFilled,
  ThunderboltOutlined,
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';

const { Text } = Typography;

const LEVEL = {
  error: <CloseCircleFilled style={{ color: '#ff4d4f' }} />,
  warn: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
  info: <InfoCircleFilled style={{ color: '#1677ff' }} />,
  ok: <CheckCircleFilled style={{ color: '#52c41a' }} />,
};

const SUMMARY = { error: 'error', warn: 'warning', ok: 'success' };

function FindingItem({ f }) {
  return (
    <List.Item style={{ alignItems: 'flex-start' }}>
      <Space align="start" size={12}>
        <span style={{ fontSize: 18, lineHeight: '22px' }}>{LEVEL[f.level] || LEVEL.info}</span>
        <div>
          <Text strong>{f.title}</Text>
          <div><Text type="secondary">{f.cause}</Text></div>
          {f.fix && <div style={{ marginTop: 4 }}><Text>处理：{f.fix}</Text></div>}
        </div>
      </Space>
    </List.Item>
  );
}

const nodeColumns = [
  { title: '节点', dataIndex: 'name' },
  {
    title: 'HTTPS',
    render: (_, r) => (r.https_error
      ? <Tag color="error">{r.https_error}</Tag>
      : r.https_status !== undefined ? <Tag color={r.https_status >= 500 ? 'error' : 'default'}>{r.https_status}</Tag> : '-'),
  },
  {
    title: 'HTTP',
    render: (_, r) => (r.http_error ? <Tag color="error">{r.http_error}</Tag> : r.http_status ?? '-'),
  },
  { title: '耗时', dataIndex: 'https_ms', render: (v) => (v ? `${v}ms` : '-') },
  { title: '缓存', dataIndex: 'cache', render: (v) => v || '-' },
  {
    title: '证书到期',
    dataIndex: 'cert_expires',
    render: (v) => (v ? new Date(v * 1000).toLocaleDateString() : '-'),
  },
];

export default function Diagnostics() {
  const { domainData } = useOutletContext();
  const [selectedHostname, setSelectedHostname] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const hostnames = domainData?.hostnames || [];
  const currentHostname = selectedHostname || hostnames[0]?.hostname;

  const handleRun = async () => {
    if (!currentHostname) return;
    setLoading(true);
    setResult(null);
    setError(null);
    const res = await cdnService.runDiagnostics({ hostname: currentHostname, domain_id: domainData.id });
    if (res.success) setResult(res.data);
    else setError(res.error || '诊断失败，请重试');
    setLoading(false);
  };

  const findings = result?.findings || [];
  const issues = findings.filter((f) => f.level !== 'ok');
  const oks = findings.filter((f) => f.level === 'ok');

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            style={{ minWidth: 300 }}
            placeholder="选择主机名"
            value={currentHostname}
            onChange={setSelectedHostname}
            options={hostnames.map((h) => ({ label: h.hostname, value: h.hostname }))}
          />
          <Button type="primary" icon={<ThunderboltOutlined />} loading={loading} onClick={handleRun} disabled={!currentHostname}>
            开始诊断
          </Button>
        </Space>
      </Card>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="诊断中..." />
        </div>
      )}

      {result && !loading && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert type={SUMMARY[result.summary?.status] || 'info'} showIcon message={<Text strong>{result.summary?.message}</Text>} />

          {issues.length > 0 && (
            <Card size="small" title="需要处理">
              <List dataSource={issues} renderItem={(f) => <FindingItem f={f} />} />
            </Card>
          )}

          <Collapse
            items={[
              oks.length > 0 && {
                key: 'ok',
                label: `通过的检查 (${oks.length})`,
                children: <List dataSource={oks} renderItem={(f) => <FindingItem f={f} />} />,
              },
              (result.nodes || []).length > 0 && {
                key: 'nodes',
                label: `节点详情 (${result.nodes.length})`,
                children: (
                  <Table size="small" rowKey="name" pagination={false} scroll={{ x: true }} columns={nodeColumns} dataSource={result.nodes} />
                ),
              },
            ].filter(Boolean)}
          />
        </Space>
      )}
    </div>
  );
}
